import React from 'react';
import {
  Quotation,
  QuotationOverhead,
  type CustomerOverheadPrintOption,
  type QuotationPrintMode,
} from '@/services/quotation.service';
import type { RevenueServiceTypeOption } from '@/utils/revenueServiceTypeRouting';
import type { ApprovalPrintSigner } from '@/services/approval.service';
import { usePrintDocumentTitle, runPrintWithDocumentTitle } from '@/utils/printDocumentTitle';
import {
  buildQuotationPagePlan,
  buildQuotationPrintModel,
  installPrintPageCss,
  measureQuotationBlocks,
  measureRenderedPrintBlocks,
  PRINT_SECTION_ATTR,
  quotationToPrintDocument,
  type BuildQuotationPrintModelInput,
  type MeasuredBlocks,
} from '@/print-engine';
import { PaginatedQuotationDocument } from './paginated/PaginatedQuotationDocument';
import './PrintableQuotation.css';
import './paginated/PaginatedQuotationPrint.css';

export interface PrintableQuotationProps {
  quotation: Quotation & {
    overheads?: QuotationOverhead[];
    profitability_snapshot?: {
      total_overhead?: number;
      loaded_total_cost?: number;
      gross_profit?: number;
      gross_profit_percentage?: number;
    } | null;
  };
  printMode?: QuotationPrintMode;
  customerOverheadDisplay?: CustomerOverheadPrintOption;
  /** Resolved display string (e.g. `MS — Manufacturing Services`). */
  revenueServiceTypeLabel?: string;
  /** RST object for manufacturing vs T&R print routing. */
  revenueServiceType?: RevenueServiceTypeOption | null;
  approvalSigners?: ApprovalPrintSigner[];
  companyInfo?: {
    name: string;
    address: string;
    phone: string;
    email: string;
    website?: string;
    taxNumber?: string;
    tradeNumber?: string;
    vatNumber?: string;
    logo?: string;
  };
  /** Company quotation defaults cover letter (rendered in a detached root, so passed in rather than queried). */
  coverLetter?: BuildQuotationPrintModelInput['coverLetter'];
}

interface PrintableQuotationInternalProps extends PrintableQuotationProps {
  onClose?: () => void;
}

function usePrintDiagnostics(): boolean {
  return React.useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('printDiag') === '1';
    } catch {
      return false;
    }
  }, []);
}

const NO_SIGNERS: ApprovalPrintSigner[] = [];

const PLACEHOLDER_COMPANY: NonNullable<PrintableQuotationProps['companyInfo']> = {
  name: 'Your Company Name',
  address: '123 Business Street, City, Country',
  phone: '+971 XX XXX XXXX',
  email: 'info@company.com',
  taxNumber: 'TRN: XXXXXXX',
};

/**
 * Sales quotation print preview — two-pass pagination (measure → page plan → composed pages).
 */
export const PrintableQuotation: React.FC<PrintableQuotationInternalProps> = ({
  quotation,
  revenueServiceTypeLabel,
  revenueServiceType,
  printMode = 'customer',
  customerOverheadDisplay = 'none',
  approvalSigners = NO_SIGNERS,
  onClose,
  companyInfo = PLACEHOLDER_COMPANY,
  coverLetter,
}) => {
  usePrintDocumentTitle(quotation.quotation_number);
  const diagnostics = usePrintDiagnostics();
  const model = React.useMemo(
    () =>
      buildQuotationPrintModel({
        quotation,
        printMode,
        customerOverheadDisplay,
        revenueServiceTypeLabel,
        revenueServiceType,
        approvalSigners,
        companyInfo,
        coverLetter,
      }),
    [
      quotation,
      printMode,
      customerOverheadDisplay,
      revenueServiceTypeLabel,
      revenueServiceType,
      approvalSigners,
      companyInfo,
      coverLetter,
    ]
  );

  // Measurements stay valid while the printable content is unchanged, even if parents re-render.
  const modelKey = React.useMemo(() => JSON.stringify(model), [model]);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [rendered, setRendered] = React.useState<{
    modelKey: string;
    measured: MeasuredBlocks;
    usableHeightPx?: number;
    phase: 'measured' | 'sized' | 'final';
  } | null>(null);
  const current = rendered?.modelKey === modelKey ? rendered : null;

  const plan = React.useMemo(
    () =>
      buildQuotationPagePlan(model, {
        diagnostics: true,
        measured: current?.measured,
        usableHeightPx: current?.usableHeightPx,
      }),
    [model, current?.measured, current?.usableHeightPx]
  );

  // Pass 2: replace text estimates with rendered block heights.
  React.useLayoutEffect(() => {
    if (current || !rootRef.current) return;
    const doc = quotationToPrintDocument(model);
    const estimated = measureQuotationBlocks(model);
    setRendered({
      modelKey,
      measured: measureRenderedPrintBlocks(rootRef.current, doc, estimated),
      phase: 'measured',
    });
  }, [model, modelKey, current]);

  // Once letterhead images and fonts are ready:
  // pass 3 plans against the real body height (artwork is shorter than the template's reserved bands);
  // pass 4 grows the last block of any page that still overflows so it moves to the next page.
  React.useEffect(() => {
    if (!current || current.phase === 'final' || !rootRef.current) return;
    const root = rootRef.current;
    let cancelled = false;
    const images = Array.from(root.querySelectorAll('img')).filter((img) => !img.complete);
    void Promise.all([
      document.fonts?.ready,
      ...images.map(
        (img) =>
          new Promise<void>((resolve) => {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          })
      ),
    ]).then(() => {
      if (cancelled) return;
      if (current.phase === 'measured') {
        const body = root.querySelector<HTMLElement>('.qtn-composed-body');
        const header = root.querySelector<HTMLImageElement>('.qtn-composed-letterhead-header');
        const footer = root.querySelector<HTMLImageElement>('.qtn-composed-letterhead-footer');
        const artworkReady = [header, footer].every((img) => img && img.naturalHeight > 0);
        if (!body || !artworkReady) {
          setRendered({ ...current, phase: 'sized' });
          return;
        }
        const style = window.getComputedStyle(body);
        const pad = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
        setRendered({
          ...current,
          // The page number sits inside the body's bottom padding, so it needs no extra reserve.
          measured: { ...current.measured, pageNumber: 0 },
          usableHeightPx: Math.floor(body.clientHeight - pad),
          phase: 'sized',
        });
        return;
      }
      const first = { ...current.measured.firstSections };
      const final = { ...current.measured.finalSections };
      let grew = false;
      root.querySelectorAll<HTMLElement>('.qtn-composed-body').forEach((body) => {
        const overflow = body.scrollHeight - body.clientHeight;
        if (overflow <= 1) return;
        const blocks = body.querySelectorAll(`[${PRINT_SECTION_ATTR}]`);
        const id = blocks[blocks.length - 1]?.getAttribute(PRINT_SECTION_ATTR);
        if (!id) return;
        const target = id in final ? final : id in first ? first : null;
        if (!target) return;
        target[id] += overflow + 4;
        grew = true;
      });
      setRendered({
        ...current,
        measured: grew ? { ...current.measured, firstSections: first, finalSections: final } : current.measured,
        phase: 'final',
      });
    });
    return () => {
      cancelled = true;
    };
  }, [modelKey, current]);

  React.useEffect(() => {
    const uninstallPageMargins = installPrintPageCss({ pageSize: 'letter' });
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      uninstallPageMargins();
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div className="qtn-print-shell">
      {onClose && (
        <div className="print-controls no-print">
          <button onClick={onClose} className="close-print-btn" title="Close Print Preview (ESC)">
            ✕ Close
          </button>
          <button
            onClick={() => {
              installPrintPageCss({ pageSize: 'letter' });
              runPrintWithDocumentTitle(quotation.quotation_number);
            }}
            className="trigger-print-btn"
            title="Open Print Dialog (Ctrl+P)"
          >
            🖨️ Print
          </button>
          <div className="qtn-print-hint">Print settings: Paper Letter · Margins None · Scale 100%</div>
          {diagnostics && plan.diagnostics ? (
            <div className="qtn-print-diag-summary">
              {plan.totalPages} page(s) · usable {Math.round(plan.diagnostics.usableHeightPx)}px
              {plan.diagnostics.warnings.length
                ? ` · ${plan.diagnostics.warnings.length} warning(s)`
                : ''}
            </div>
          ) : null}
        </div>
      )}

      <div ref={rootRef}>
        <PaginatedQuotationDocument model={model} plan={plan} diagnostics={diagnostics} />
      </div>
    </div>
  );
};
